import { buildPrompt } from '@/ai/utils/promptBuilder'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { createUserMessage, createSystemMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { commandNotes, getCardTypeInstruction, readModelNotes } from '@/ai/prompts/common/instructions'

export const buildSingleEventPrompt = ({
  parentEventId,
  workflow,
  cardTypes
}) => {
  const systemMessageSections = []
  systemMessageSections.push(systemMessages.EVENT_MODELING_DDD_EXPERT)
  const systemContent = buildPrompt(systemMessageSections)

  const messages = []
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  const laneMap = new Map((workflow.lanes || []).map((l) => [l.id, l.name]))
  const clickedNode = workflow.domainEvents.find((e) => e.id === parentEventId)
  const isGateway = clickedNode?.type === 'bpmn:ExclusiveGateway'

  let parentEvent
  let requestContent

  if (isGateway) {
    parentEvent = workflow.domainEvents.find((e) => e.id === clickedNode.parents[0])

    const originalChildNodes = workflow.domainEvents.filter((e) => e.parents?.includes(clickedNode.id))

    const existingBranchesText = originalChildNodes
      .map((node) => {
        let conditionLabel = null
        if (Array.isArray(node.conditionLabels)) {
          const labelObj = node.conditionLabels.find((cl) => cl.parentId === clickedNode.id)
          if (labelObj && labelObj.name) {
            conditionLabel = labelObj.name
          }
        }
        if (!conditionLabel && node.conditionLabel) {
          conditionLabel = node.conditionLabel
        }

        let description = `"${node.description}"`
        if (conditionLabel) {
          description += ` with condition "${conditionLabel}"`
        }

        return description
      })
      .filter(Boolean)
      .join(', ')

    requestContent = `You are adding a new event that represents a new branch originating from the decision gateway named "${clickedNode.description}". This new event should occur after the event "${parentEvent?.description}". The decision already has the following branches with their conditions: ${existingBranchesText}. Crucially, you must also provide a 'conditionLabel' for this new branch. This label defines the condition for this path to be taken (e.g., "Yes", "No", "Approved", "Rejected", or "Amount > 1000") and it should be different from the existing ones. Suggest one more relevant domain event for this new branch in the business process. The step must reflect what is typically happening in the process (a command invoked by an actor on an aggregate), at this point on the timeline. Follow the rules stated for Domain Events.`
  } else {
    parentEvent = workflow.domainEvents.find((e) => e.id === parentEventId)
    const childEvents = workflow.domainEvents.filter((e) => e.parents?.includes(parentEventId))
    const parentName = parentEvent?.description || ''
    const parentRole = laneMap.get(parentEvent?.laneId) || ''

    requestContent = `Suggest one more domain event to be added to the Current Workflow, after event "${parentName}" (${parentRole})${childEvents.length > 0 ? ` and before event "${childEvents[0].description}"` : ''}, that is relevant to the business process, workflow or project scope. The step must reflect what is typically happening in the process (a command invoked by an actor on an aggregate), at this point on the timeline. Follow the rules stated for Domain Events.`
  }

  let cardTypeInstructions = ''

  cardTypes.forEach((cardType) => {
    cardTypeInstructions += getCardTypeInstruction(cardType, 'Also add one')
  })

  const commandCardType = diagramHelpers.methods.getCommandCardType(cardTypes)
  const queryCardType = diagramHelpers.methods.getQueryCardType(cardTypes)

  promptSections.push({
    title: 'Request',
    content: requestContent,
    spacingAfter: (commandCardType || queryCardType || cardTypeInstructions) ? 1 : 2
  })

  if (commandCardType) {
    promptSections.push({
      content: 'Also generate the set of input fields required to invoke its associated command on the aggregate. Treat each set as a simple input form (wireframe-style) that the actor or Automation role fills in.',
      spacingAfter: (queryCardType || cardTypeInstructions) ? 1 : 2
    })
  }

  if (queryCardType) {
    promptSections.push({
      content: 'Also generate a Read Model (or Query Model) for the actor (human or system) about to make the necessary decisions to take action and complete the step. The purpose of the Read Model (or Query Model) is to define a query (for one or many items) that displays the current system state in a way that is relevant to the actor BEFORE doing the task at hand. Provide the Read Model as a set of data fields to be displayed to the actor BEFORE invoking the command on the aggregate root. Usually, between 2 and 8 fields are sufficient, but it can be more or less. Tag each data field with INDEXED if it should be indexed for filtering or search. In most of the cases the generated data fields should match with the data fields of the entity and include the id field of the entity. An attribute can be a primitive or a reference to a non-primitive type like an associated entity. An attribute or field name must be in camel case format (e.g., \'productName\' instead of \'Product Name\'). An attribute or field name must be at most 30 characters long. For the Read Model, provide the name of the entity that is queried to build it. If the entity exists in the workflow specification, use the exact same name. If it does not exist, provide a new entity name in human-readable format (not camel case). Also determine the cardinality: use "one-to-one" if the query returns a single item (e.g., viewing details of a specific order), or "one-to-many" if it returns a list of items (e.g., listing available products). Default to "one-to-many" if unclear.',
      spacingAfter: 2
    })
  }

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  if (commandCardType) {
    promptSections.push({
      title: 'Very Important Considerations for Command',
      contentType: 'bullet',
      content: commandNotes
    })
  }

  if (queryCardType) {
    promptSections.push({
      title: 'Very Important Considerations for Read Model',
      contentType: 'bullet',
      content: readModelNotes,
      spacingAfter: cardTypeInstructions ? 1 : 2
    })
  }

  if (cardTypeInstructions) {
    promptSections.push({ content: cardTypeInstructions })
  }

  const exampleDomainEvent = {
    role: 'Role X',
    name: 'Approved invoice',
    commandExample: examples.commandExampleCreateOrder,
    readModelExample: examples.readModelExampleCart,
    queriedEntity: 'Order',
    cardinality: 'one-to-one'
  }

  if (isGateway) {
    exampleDomainEvent.conditionLabel = 'New Condition'
  }

  const eventExample = {
    event: getEventExample(exampleDomainEvent, cardTypes)
  }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting('event', JSON.stringify(eventExample))
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
